import {
  type DeductUsageResponseDto,
  LedgerApi,
  type LedgerControllerGetUsageHistoryRequest,
  type OffsetPaginatedUsageResponseDto,
  type RecordUsageDto,
} from '@saaga/billingv2-sdk'
import { updateTenantBalance } from '@saaga/shared/store/slices/billing.slice'
import { useAppDispatch } from '@saaga/shared/store/store'
import { useCallback } from 'react'

import { useBillingConfig } from './use-billing-config.hook'

export interface RecordUsageParams {
  priceCogs: number
  priceSystemMargin: number
  priceMarketplaceMargin: number
  conversationId?: string
  messageId?: string
  agencyName?: string
  agencyVersion?: string
  stepCount?: number
  stepType?: string
  agentName?: string
  toolName?: string
  llmModel?: string
  usageCount?: number
  idempotencyKey?: string
  metadata?: Record<string, unknown>
}

export type UsageHistoryParams = Omit<LedgerControllerGetUsageHistoryRequest, 'xTenantId'>

const buildRecordUsageDto = (params: RecordUsageParams): RecordUsageDto => ({
  priceCogs: params.priceCogs.toString(),
  priceSystemMargin: params.priceSystemMargin.toString(),
  priceMarketplaceMargin: params.priceMarketplaceMargin.toString(),
  conversationId: params.conversationId,
  messageId: params.messageId,
  agencyName: params.agencyName,
  agencyVersion: params.agencyVersion,
  stepCount: params.stepCount,
  stepType: params.stepType,
  agentName: params.agentName,
  toolName: params.toolName,
  llmModel: params.llmModel,
  usageCount: params.usageCount?.toString(),
  idempotencyKey: params.idempotencyKey,
  metadata: params.metadata as object,
})

export interface UseLedgerApiReturn {
  getLedgerClient: () => Promise<LedgerApi>
  getBalance: () => Promise<number>
  refetchBalance: () => Promise<void>
  getUsageHistory: (params?: UsageHistoryParams) => Promise<OffsetPaginatedUsageResponseDto>
  recordUsage: (params: RecordUsageParams) => Promise<DeductUsageResponseDto>
}

export const useLedgerApi = (): UseLedgerApiReturn => {
  const { createConfig } = useBillingConfig()
  const dispatch = useAppDispatch()

  const getLedgerClient = useCallback(async () => {
    const config = await createConfig()
    return new LedgerApi(config)
  }, [createConfig])

  const getBalance = useCallback(async (): Promise<number> => {
    const client = await getLedgerClient()
    const response = await client.ledgerControllerGetBalance()
    return Number.parseFloat(response.amount) / 1_000_000
  }, [getLedgerClient])

  const refetchBalance = useCallback(async (): Promise<void> => {
    try {
      const client = await getLedgerClient()
      const response = await client.ledgerControllerGetBalance()
      dispatch(updateTenantBalance(response.amount))
    } catch {
      // Silently fail — UI shows stale data but won't break
    }
  }, [getLedgerClient, dispatch])

  const getUsageHistory = useCallback(
    async (params: UsageHistoryParams = {}): Promise<OffsetPaginatedUsageResponseDto> => {
      const client = await getLedgerClient()
      return client.ledgerControllerGetUsageHistory(params)
    },
    [getLedgerClient]
  )

  const recordUsage = useCallback(
    async (params: RecordUsageParams): Promise<DeductUsageResponseDto> => {
      const client = await getLedgerClient()
      return client.ledgerControllerRecordUsage({ recordUsageDto: buildRecordUsageDto(params) })
    },
    [getLedgerClient]
  )

  return { getLedgerClient, getBalance, refetchBalance, getUsageHistory, recordUsage }
}
